/**
 * Phase 187 — Champs cross-liaison (Fournisseur / Produit / Raison)
 * pour le formulaire d'ajout d'entretien matériel.
 *
 * Props :
 *   value : { supplier_id, supplier_product_id, reason_id }
 *   onChange : (patch) => void
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";

const NONE = "__none__";

export const MaintenanceCrossLinksFields = ({ value, onChange }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [creatingReason, setCreatingReason] = useState(false);
  const [newReasonName, setNewReasonName] = useState("");

  useEffect(() => {
    api.getSuppliers()
      .then((res) => {
        const data = res.data;
        const items = Array.isArray(data) ? data : (data.items || []);
        setSuppliers(items);
      })
      .catch(() => setSuppliers([]));
    api.getMaintenanceReasons()
      .then((res) => setReasons(res.data || []))
      .catch(() => setReasons([]));
  }, []);

  // Load products when a supplier is selected
  useEffect(() => {
    if (!value.supplier_id) {
      setProducts([]);
      return;
    }
    api.getSupplierProducts(value.supplier_id)
      .then((res) => setProducts(res.data || []))
      .catch(() => setProducts([]));
  }, [value.supplier_id]);

  const handleCreateReason = async () => {
    const name = newReasonName.trim();
    if (!name) return;
    try {
      const res = await api.createMaintenanceReason({ name });
      const created = res.data;
      setReasons((prev) => {
        const exists = prev.find((r) => r.id === created.id);
        return exists ? prev : [...prev, created].sort((a, b) => a.name.localeCompare(b.name));
      });
      onChange({ reason_id: created.id });
      setCreatingReason(false);
      setNewReasonName("");
      toast.success("Raison ajoutée");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="space-y-3 p-3 border rounded-sm bg-blue-50/40">
      <p className="text-xs font-medium text-blue-900">Liaison fournisseur / produit / motif</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Fournisseur / Prestataire</Label>
          <Select
            value={value.supplier_id || NONE}
            onValueChange={(v) => {
              const sid = v === NONE ? null : v;
              // Si on change de fournisseur, on invalide le produit sélectionné
              onChange({ supplier_id: sid, supplier_product_id: null });
            }}
          >
            <SelectTrigger data-testid="maint-supplier"><SelectValue placeholder="— Aucun —" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>— Aucun —</SelectItem>
              {suppliers.map((s) => (
                <SelectItem key={s.id} value={s.id} data-testid={`maint-supplier-opt-${s.id}`}>
                  {s.company_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Produit</Label>
          <Select
            value={value.supplier_product_id || NONE}
            onValueChange={(v) => onChange({ supplier_product_id: v === NONE ? null : v })}
            disabled={!value.supplier_id}
          >
            <SelectTrigger data-testid="maint-product">
              <SelectValue placeholder={value.supplier_id ? "— Aucun —" : "Choisir un fournisseur d'abord"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>— Aucun —</SelectItem>
              {products.map((p) => (
                <SelectItem key={p.id} value={p.id} data-testid={`maint-product-opt-${p.id}`}>
                  {p.name}{p.reference ? ` (${p.reference})` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <Label className="text-xs">Raison / Motif</Label>
          {!creatingReason && (
            <Button type="button" size="sm" variant="ghost" className="h-6 text-xs" onClick={() => setCreatingReason(true)} data-testid="maint-add-reason-btn">
              <Plus className="h-3 w-3 mr-1" /> Nouvelle raison
            </Button>
          )}
        </div>
        {creatingReason ? (
          <div className="flex gap-2">
            <Input
              autoFocus
              placeholder="Ex: Pièces détachées, Garantie, Consommables…"
              value={newReasonName}
              onChange={(e) => setNewReasonName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleCreateReason(); } }}
              data-testid="maint-new-reason-input"
            />
            <Button type="button" size="sm" onClick={handleCreateReason} data-testid="maint-new-reason-save">OK</Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => { setCreatingReason(false); setNewReasonName(""); }}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <Select
            value={value.reason_id || NONE}
            onValueChange={(v) => onChange({ reason_id: v === NONE ? null : v })}
          >
            <SelectTrigger data-testid="maint-reason"><SelectValue placeholder="— Aucune —" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>— Aucune —</SelectItem>
              {reasons.map((r) => (
                <SelectItem key={r.id} value={r.id} data-testid={`maint-reason-opt-${r.id}`}>
                  {r.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
    </div>
  );
};

export default MaintenanceCrossLinksFields;
